import { useMasquerade } from 'modules/oidc/useAuthenticated';
import api from 'modules/queries/api';
import { useCallback, useMemo, useRef } from 'react';
import useAccountUserStatedataAdd from './useAdd';
import useAccountUserStatedataListforkey from './useListForKey';
import useAccountUserStatedataUpdate from './useUpdate';

// @ts-ignore
globalThis.resetMeta = () =>
	api.account.user.stateData.apiRaw.replace({ key: 'meta', data: '[]' });

// @ts-ignore
globalThis.resetAccountMeta = () => {
	api.account.stateData.apiRaw.replace({ key: 'meta', data: '[]' });
};

export interface UserMetaStateStatusI {
	isLoading: boolean;
	isFetching: boolean;
	isBusy: boolean;
	isError: boolean;
	error: unknown;
}

type DataRow<T> =
	| {
			uuid: string;
			updated?: number;
			name: string;
			value: T;
	  }
	| undefined;

/**
 * Account User Statedata helper hook
 */
export const useUserMetaState = <T>(
	name: string,
	initialValue: T,
	options?: {
		/**
		 * @default true
		 * By default, masquerade users set meta state separate from regular users. Set this to false to have masqueraders and regular users set meta state in the same key
		 */
		safe?: boolean;
		/**
		 * @default true
		 * Set to false to hold off fetching the meta state
		 */
		enabled?: boolean;
	},
): [T, (value: T) => void, UserMetaStateStatusI] => {
	const isMasquerade = useMasquerade();
	const safe = options?.safe ?? true;
	const masqueradePrefix = safe ? 'masquerade_' : '';
	const nameAdjusted = isMasquerade ? `${masqueradePrefix}${name}` : name;

	const {
		data: listData,
		isLoading,
		isFetching,
		isError,
		error,
	} = useAccountUserStatedataListforkey(
		{ key: 'meta' },
		{ enabled: options?.enabled },
	);
	const { mutate: addMutate, isLoading: isAddLoading } =
		useAccountUserStatedataAdd();
	const { mutate: updateMutate, isLoading: isUpdateLoading } =
		useAccountUserStatedataUpdate();

	const dataRow = useMemo(() => {
		if (!(listData?.data instanceof Array)) return undefined;

		// First try to find the nameAdjusted version
		const nameAdjustedMatches = listData.data.filter(
			(row) => row.name === nameAdjusted,
		) as NonNullable<DataRow<T>>[];

		// If masquerading and no nameAdjusted version found, fall back to original name
		const matches =
			isMasquerade && nameAdjustedMatches.length === 0
				? (listData.data.filter((row) => row.name === name) as NonNullable<
						DataRow<T>
					>[])
				: nameAdjustedMatches;

		// Accidents happen, pick the most recent updated
		return matches.sort((a, b) => (b?.updated || 0) - (a?.updated || 0))[0];
	}, [nameAdjusted, name, isMasquerade, listData]);
	const uuid = dataRow?.uuid;

	// Store setValue in a ref so it doesn't bother downstream dependancies
	const setValueRef = useRef<((value: T) => void) | undefined>(undefined);

	setValueRef.current = (value) => {
		if (!uuid) {
			addMutate({
				key: 'meta',
				data: JSON.stringify({
					value,
					name: nameAdjusted,
					updated: Date.now(),
				}),
			});
		} else {
			updateMutate({
				key: 'meta',
				data: JSON.stringify({
					uuid,
					value,
					name: nameAdjusted,
					updated: Date.now(),
				}),
			});
		}
	};

	const setValue = useCallback((value) => {
		setValueRef.current?.(value);
	}, []);

	const isBusy = isFetching || isAddLoading || isUpdateLoading;
	const value = !dataRow ? initialValue : dataRow.value;

	return [value, setValue, { isLoading, isFetching, isBusy, isError, error }];
};

export default useUserMetaState;
